import { useState, useMemo, useEffect } from "react";
import { Calculator, Flame, Scale } from "lucide-react";

/* ------------------ Helpers ------------------ */
function parseHeightToCm(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  // cm
  const cmMatch = s.match(/^(\d+(?:\.\d+)?)\s*cm$/);
  if (cmMatch) return parseFloat(cmMatch[1]);
  // pure number → assume cm if > 90, else feet
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    return n > 90 ? n : n * 30.48;
  }
  // feet/inches: 5'10" or 5ft10in or 5 10
  const ftIn = s.match(/^(\d+)\s*(?:'|ft)\s*(\d+(?:\.\d+)?)?\s*(?:"|in)?$/);
  if (ftIn) {
    const ft = parseInt(ftIn[1], 10);
    const inch = ftIn[2] ? parseFloat(ftIn[2]) : 0;
    return ft * 30.48 + inch * 2.54;
  }
  const ftSpace = s.match(/^(\d+)\s+(\d+(?:\.\d+)?)$/);
  if (ftSpace) {
    return parseInt(ftSpace[1], 10) * 30.48 + parseFloat(ftSpace[2]) * 2.54;
  }
  return null;
}

function parseWeightToKg(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  const kg = s.match(/^(\d+(?:\.\d+)?)\s*kg$/);
  if (kg) return parseFloat(kg[1]);
  const lb = s.match(/^(\d+(?:\.\d+)?)\s*(lb|lbs)$/);
  if (lb) return parseFloat(lb[1]) * 0.453592;
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    return n > 130 ? n * 0.453592 : n; // assume lb if >130 (most cm-tall users < 130kg)
  }
  return null;
}

const fmt = (n, d = 0) =>
  n == null || isNaN(n) ? "—" : Number(n).toFixed(d);

/* ------------------ Visual Scale Gauge ------------------ */
function ScaleGauge({ stops, value, min, max, valueLabel }) {
  // stops: [{ end: number, color: string, label: string }]
  // Map value to 0..100% within [min,max]
  const pct =
    value == null
      ? null
      : Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

  return (
    <div className="mt-6">
      <div className="relative h-3 flex">
        {stops.map((s, i) => {
          const prevEnd = i === 0 ? min : stops[i - 1].end;
          const widthPct = ((s.end - prevEnd) / (max - min)) * 100;
          return (
            <div
              key={i}
              style={{ width: `${widthPct}%`, background: s.color }}
              className="h-full"
            />
          );
        })}
        {pct !== null && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-500"
            style={{ left: `${pct}%` }}
          >
            <div className="w-1 h-7 bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 whitespace-nowrap">
              {valueLabel}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-between mt-2 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
        {stops.map((s, i) => (
          <span key={i}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

/* ------------------ BMI Tool ------------------ */
function BmiTool() {
  const [h, setH] = useState("");
  const [w, setW] = useState("");

  const result = useMemo(() => {
    const cm = parseHeightToCm(h);
    const kg = parseWeightToKg(w);
    if (!cm || !kg || cm < 100 || kg < 25) return null;
    const m = cm / 100;
    const bmi = kg / (m * m);
    let category = "Normal";
    let color = "#16A34A";
    if (bmi < 18.5) {
      category = "Underweight";
      color = "#3B82F6";
    } else if (bmi < 25) {
      category = "Normal";
      color = "#16A34A";
    } else if (bmi < 30) {
      category = "Overweight";
      color = "#D4AF37";
    } else {
      category = "Obese";
      color = "#DC2626";
    }
    return { bmi, category, color };
  }, [h, w]);

  return (
    <div data-testid="bmi-tool" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Height
          </label>
          <input
            data-testid="bmi-height"
            className="input-omni"
            placeholder='e.g., 5&apos;10&quot; or 178cm'
            value={h}
            onChange={(e) => setH(e.target.value)}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Weight
          </label>
          <input
            data-testid="bmi-weight"
            className="input-omni"
            placeholder="e.g., 185 lb or 84 kg"
            value={w}
            onChange={(e) => setW(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
        <div className="flex items-end justify-between mb-1">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              Your BMI
            </div>
            <div
              data-testid="bmi-value"
              className="font-display text-6xl text-white leading-none"
              style={result ? { color: result.color } : undefined}
            >
              {result ? fmt(result.bmi, 1) : "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              Category
            </div>
            <div
              data-testid="bmi-category"
              className="font-display text-2xl"
              style={{ color: result ? result.color : "#A3A3A3" }}
            >
              {result ? result.category : "—"}
            </div>
          </div>
        </div>

        <ScaleGauge
          min={15}
          max={40}
          value={result?.bmi}
          valueLabel={result ? `BMI ${fmt(result.bmi, 1)}` : ""}
          stops={[
            { end: 18.5, color: "#3B82F6", label: "Under" },
            { end: 25, color: "#16A34A", label: "Normal" },
            { end: 30, color: "#D4AF37", label: "Over" },
            { end: 40, color: "#DC2626", label: "Obese" },
          ]}
        />
      </div>
      <p className="text-xs text-neutral-500 leading-relaxed">
        BMI is a rough screen — it doesn't account for muscle mass. Athletes
        often score "overweight" while shredded.
      </p>
    </div>
  );
}

/* ------------------ Calorie / TDEE Tool ------------------ */
const ACTIVITY = [
  { v: 1.2, l: "Sedentary (desk, no training)" },
  { v: 1.375, l: "Light (1–3 days/wk)" },
  { v: 1.55, l: "Moderate (3–5 days/wk)" },
  { v: 1.725, l: "Heavy (6–7 days/wk)" },
  { v: 1.9, l: "Athlete (2x/day, manual labor)" },
];

const GOAL = [
  { v: -0.2, l: "Aggressive Cut", note: "−20% kcal" },
  { v: -0.1, l: "Lean Cut", note: "−10% kcal" },
  { v: 0, l: "Maintain", note: "0%" },
  { v: 0.1, l: "Lean Bulk", note: "+10% kcal" },
  { v: 0.2, l: "Aggressive Bulk", note: "+20% kcal" },
];

function CalorieTool() {
  const [sex, setSex] = useState("male");
  const [age, setAge] = useState("");
  const [h, setH] = useState("");
  const [w, setW] = useState("");
  const [act, setAct] = useState(1.55);
  const [goalAdj, setGoalAdj] = useState(0);

  const result = useMemo(() => {
    const cm = parseHeightToCm(h);
    const kg = parseWeightToKg(w);
    const a = parseInt(age, 10);
    if (!cm || !kg || !a || a < 13 || a > 100) return null;
    // Mifflin-St Jeor BMR
    const bmr =
      sex === "male"
        ? 10 * kg + 6.25 * cm - 5 * a + 5
        : 10 * kg + 6.25 * cm - 5 * a - 161;
    const tdee = bmr * act;
    const target = tdee * (1 + goalAdj);
    return { bmr, tdee, target };
  }, [sex, age, h, w, act, goalAdj]);

  // Save TDEE result to localStorage so FoodLog can auto-pull it
  useEffect(() => {
    if (result) {
      try {
        localStorage.setItem(
          "omnimuscle_tdee_v1",
          JSON.stringify({ target: result.target, tdee: result.tdee, bmr: result.bmr })
        );
      } catch {
        // ignore
      }
    }
  }, [result]);

  return (
    <div data-testid="calorie-tool" className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Sex
          </label>
          <div className="flex gap-2">
            {[
              { v: "male", l: "Male" },
              { v: "female", l: "Female" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`cal-sex-${opt.v}`}
                onClick={() => setSex(opt.v)}
                className={`flex-1 px-3 py-3 border-2 text-sm font-semibold transition-all ${
                  sex === opt.v
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                    : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Age
          </label>
          <input
            data-testid="cal-age"
            className="input-omni"
            placeholder="e.g., 32"
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Height
          </label>
          <input
            data-testid="cal-height"
            className="input-omni"
            placeholder='5&apos;10&quot; or 178cm'
            value={h}
            onChange={(e) => setH(e.target.value)}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Weight
          </label>
          <input
            data-testid="cal-weight"
            className="input-omni"
            placeholder="185 lb or 84 kg"
            value={w}
            onChange={(e) => setW(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
          Activity Level
        </label>
        <select
          data-testid="cal-activity"
          className="input-omni"
          value={act}
          onChange={(e) => setAct(parseFloat(e.target.value))}
        >
          {ACTIVITY.map((a) => (
            <option key={a.v} value={a.v}>
              {a.l}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
          Goal
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {GOAL.map((g) => (
            <button
              key={g.v}
              data-testid={`cal-goal-${g.v}`}
              onClick={() => setGoalAdj(g.v)}
              className={`p-3 border-2 transition-all text-left ${
                goalAdj === g.v
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                  : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
              }`}
            >
              <div className="font-display text-sm leading-tight">{g.l}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">{g.note}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
        <div className="grid grid-cols-3 gap-4 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              BMR
            </div>
            <div
              data-testid="cal-bmr"
              className="font-display text-3xl md:text-4xl text-neutral-300"
            >
              {fmt(result?.bmr)}
            </div>
            <div className="text-[10px] text-neutral-600">kcal at rest</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              TDEE
            </div>
            <div
              data-testid="cal-tdee"
              className="font-display text-3xl md:text-4xl text-white"
            >
              {fmt(result?.tdee)}
            </div>
            <div className="text-[10px] text-neutral-600">kcal maintain</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Target
            </div>
            <div
              data-testid="cal-target"
              className="font-display text-4xl md:text-5xl text-[#D4AF37]"
            >
              {fmt(result?.target)}
            </div>
            <div className="text-[10px] text-neutral-600">kcal/day to hit goal</div>
          </div>
        </div>

        <ScaleGauge
          min={1200}
          max={4500}
          value={result?.target}
          valueLabel={result ? `${fmt(result.target)} kcal` : ""}
          stops={[
            { end: 1800, color: "#3B82F6", label: "Cut" },
            { end: 2500, color: "#16A34A", label: "Lean" },
            { end: 3200, color: "#D4AF37", label: "Maintain" },
            { end: 4500, color: "#DC2626", label: "Bulk" },
          ]}
        />
      </div>
      <p className="text-xs text-neutral-500 leading-relaxed">
        Mifflin–St Jeor formula × activity multiplier × goal adjustment.
        Track for 2 weeks, then adjust ±200 kcal based on real-world results.
      </p>
    </div>
  );
}

/* ------------------ Ideal Weight Tool ------------------ */
function IdealWeightTool() {
  const [h, setH] = useState("");
  const [sex, setSex] = useState("male");

  const result = useMemo(() => {
    const cm = parseHeightToCm(h);
    if (!cm || cm < 130 || cm > 230) return null;
    // Devine + Robinson, in kg
    const inchesOver5ft = Math.max(0, cm / 2.54 - 60);
    const devine =
      sex === "male" ? 50 + 2.3 * inchesOver5ft : 45.5 + 2.3 * inchesOver5ft;
    const robinson =
      sex === "male" ? 52 + 1.9 * inchesOver5ft : 49 + 1.7 * inchesOver5ft;
    const lower = Math.min(devine, robinson);
    const upper = Math.max(devine, robinson);
    // Athletic upper range — bodybuilders typically run +10-15% above Devine
    const athletic = devine * 1.12;
    return {
      lower_kg: lower,
      upper_kg: upper,
      athletic_kg: athletic,
      lower_lb: lower * 2.20462,
      upper_lb: upper * 2.20462,
      athletic_lb: athletic * 2.20462,
    };
  }, [h, sex]);

  return (
    <div data-testid="ideal-tool" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Sex
          </label>
          <div className="flex gap-2">
            {[
              { v: "male", l: "Male" },
              { v: "female", l: "Female" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`ideal-sex-${opt.v}`}
                onClick={() => setSex(opt.v)}
                className={`flex-1 px-3 py-3 border-2 text-sm font-semibold transition-all ${
                  sex === opt.v
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                    : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
            Height
          </label>
          <input
            data-testid="ideal-height"
            className="input-omni"
            placeholder='5&apos;10&quot; or 178cm'
            value={h}
            onChange={(e) => setH(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              Standard Range
            </div>
            <div
              data-testid="ideal-range"
              className="font-display text-3xl md:text-4xl text-white"
            >
              {result
                ? `${fmt(result.lower_lb)}–${fmt(result.upper_lb)} lb`
                : "—"}
            </div>
            <div className="text-[10px] text-neutral-600">
              {result ? `${fmt(result.lower_kg, 1)}–${fmt(result.upper_kg, 1)} kg` : ""}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Athletic Build
            </div>
            <div
              data-testid="ideal-athletic"
              className="font-display text-3xl md:text-4xl text-[#D4AF37]"
            >
              {result ? `${fmt(result.athletic_lb)} lb` : "—"}
            </div>
            <div className="text-[10px] text-neutral-600">
              {result ? `${fmt(result.athletic_kg, 1)} kg` : ""}
            </div>
          </div>
        </div>

        <ScaleGauge
          min={result ? Math.max(result.lower_lb - 20, 80) : 80}
          max={result ? result.athletic_lb + 30 : 280}
          value={result?.athletic_lb}
          valueLabel={result ? "Athletic Target" : ""}
          stops={[
            {
              end: result ? result.lower_lb : 100,
              color: "#3B82F6",
              label: "Light",
            },
            {
              end: result ? result.upper_lb : 200,
              color: "#16A34A",
              label: "Normal",
            },
            {
              end: result ? result.athletic_lb + 30 : 280,
              color: "#D4AF37",
              label: "Athletic",
            },
          ]}
        />
      </div>
      <p className="text-xs text-neutral-500 leading-relaxed">
        Devine & Robinson formulas, plus a +12% athletic adjustment for muscular,
        densely-built physiques. Bodybuilders often exceed this range.
      </p>
    </div>
  );
}

/* ------------------ Section Wrapper ------------------ */
const TABS = [
  { id: "bmi", label: "BMI", icon: Scale, Component: BmiTool },
  { id: "calories", label: "Daily Calories", icon: Flame, Component: CalorieTool },
  { id: "ideal", label: "Ideal Weight", icon: Calculator, Component: IdealWeightTool },
];

export default function Tools() {
  const [tab, setTab] = useState("bmi");
  const Active = TABS.find((t) => t.id === tab).Component;

  return (
    <section
      id="tools"
      data-testid="tools-section"
      className="py-24 md:py-36 bg-[#080808]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-3">
            Free Tools · No Sign-Up
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-4">
            Know Your <span className="text-[#D4AF37]">Numbers.</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Three battle-tested calculators every athlete should run before
            building a plan. Type, see your scale, then come back for the
            personalized protocol.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tools-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 border-2 font-accent uppercase text-xs tracking-widest font-bold transition-all ${
                  active
                    ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                    : "border-zinc-800 text-neutral-300 hover:border-[#D4AF37]"
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Active calculator */}
        <div className="bg-[#0F0F0F] border border-zinc-800 p-6 md:p-10">
          <Active />
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <p className="text-neutral-400 mb-4">
            Numbers in hand? Now get the full protocol.
          </p>
          <button
            data-testid="tools-cta-coach"
            onClick={() =>
              document
                .getElementById("ai-coach")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-omni btn-red"
          >
            Get My AI Plan
          </button>
        </div>
      </div>
    </section>
  );
}
